import {useEffect, useMemo, useState} from 'react';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import {signIn as signInThunk} from '../store/slices/authThunks';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {authErrorSelector, authLoadingSelector, isAuthenticatedSelector} from '../store/selectors/auth.selectors';
import {readAuthRedirectErrorParams} from '../lib/authRedirectError';
import AuthCard from '../components/auth/AuthCard';
import {controlClass, fieldError, primaryButtonClass, Spinner} from '../components/formStyles';

interface SignInFormValues {
    email: string;
    password: string;
}

const signInSchema = Yup.object({
  email: Yup.string()
    .email('Please enter a valid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required'),
});

/**
 * Sign-in only. Creating an account is `/signup`, which is a Paddle checkout — it shares no fields
 * with this screen, so the old "same form in two modes" arrangement is gone.
 *
 * Google is still offered because existing accounts use it, but it can no longer *create* one:
 * Supabase signup is disabled, so an unknown Google address comes back here with an error rather
 * than silently getting an account that never paid. `readRedirectError` is what turns that into a
 * sentence pointing at the trial.
 */
function readRedirectError(search: string, hash: string): string | null {
  const {error, description} = readAuthRedirectErrorParams(search, hash);
  const readable = description || error;
  if (!readable) {
    return null;
  }

  if (/signups? not allowed|signup is disabled/i.test(readable)) {
    return 'There is no CensorMe account for that address yet. Accounts start with a trial — '
      + 'sign up first, then Google sign-in will work.';
  }
  return readable;
}

export default function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const authLoading = useAppSelector(authLoadingSelector);
  const authError = useAppSelector(authErrorSelector);
  const isAuthenticated = useAppSelector(isAuthenticatedSelector);

  const redirectError = useMemo(
    () => readRedirectError(location.search, location.hash),
    [location.search, location.hash]
  );
  const [localError, setLocalError] = useState<string | null>(null);

  const error = localError || redirectError || authError;
  // Where the user was heading before the guard bounced them here.
  const redirectTo = (location.state as { from?: string } | null)?.from || '/welcome';

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, {replace: true});
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const handleSubmit = async (values: SignInFormValues, {setSubmitting}: any) => {
    setLocalError(null);

    try {
      await dispatch(signInThunk({email: values.email, password: values.password})).unwrap();
      // The effect above navigates once the session lands in the store.
    } catch (err: any) {
      const errorMessage = err?.message || err || 'Something went wrong. Please try again.';
      console.error('[Auth] Error:', errorMessage);
      setLocalError(String(errorMessage));
    } finally {
      setSubmitting(false);
    }
  };


  if (authLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-950">
        <div className="flex items-center gap-2.5 text-sm text-gray-400">
          <span className="h-4 w-4 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
          Loading…
        </div>
      </div>
    );
  }

  return (
    <AuthCard
      title="Sign in"
      subtitle="Welcome back — pick up where you left off."
      error={error}
      footer={
        <Link to="/" className="text-xs text-gray-600 hover:text-gray-400 transition-colors">
          ← Back to censorme.app
        </Link>
      }
    >
      <Formik
        initialValues={{email: '', password: ''}}
        validationSchema={signInSchema}
        onSubmit={handleSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({values, isSubmitting}) => (
          <Form className="mt-5 space-y-3.5">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="email">
                Email
              </label>
              <Field
                id="email"
                type="text"
                name="email"
                autoComplete="email"
                className={controlClass}
                placeholder="you@example.com"
              />
              <ErrorMessage name="email">{fieldError}</ErrorMessage>
            </div>

            <div>
              <div className="mb-1 flex items-baseline justify-between gap-3">
                <label className="block text-xs font-medium text-gray-300" htmlFor="password">
                  Password
                </label>
                {/* Carry whatever they have typed across, so the reset screen opens pre-filled. */}
                <Link
                  to={`/forgot-password${values.email ? `?email=${encodeURIComponent(values.email)}` : ''}`}
                  className="text-xs text-gray-500 hover:text-indigo-400 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
              <Field
                id="password"
                type="password"
                name="password"
                autoComplete="current-password"
                className={controlClass}
                placeholder="••••••••"
              />
              <ErrorMessage name="password">{fieldError}</ErrorMessage>
            </div>

            <button type="submit" disabled={isSubmitting} className={`w-full ${primaryButtonClass}`}>
              {isSubmitting && <Spinner />}
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </button>
          </Form>
        )}
      </Formik>

      <p className="mt-5 text-center text-sm text-gray-500">
        Don’t have an account?{' '}
        <Link to="/signup" className="font-medium text-indigo-400 hover:text-indigo-300 transition-colors">
          Start a trial
        </Link>
      </p>
    </AuthCard>
  );
}
